import React from "react";
import { parseFrontmatter } from "@/lib/markdownUtils";

/**
 * Markdown renderer for assistant + Vault content.
 * Supports: headings, bold/italic, inline + fenced code, blockquotes, lists,
 * task lists, links, paragraphs, horizontal rules — plus Vault Phase 2:
 *   - [[wiki-links]] and [[wiki-links|alias]]  (clickable, resolved vs unresolved)
 *   - ![[embeds]] on their own line            (inline the linked note)
 *   - Obsidian-style callouts > [!note] Title
 *
 * `docs` (array of {id, name, folder, markdown}) powers wiki-link resolution;
 * `onOpenDoc(id)` is invoked when a resolved wiki-link / embed header is clicked.
 */

function escape(s) {
  return String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
}

const CALLOUTS = {
  note: { label: "Note", color: "var(--ph-brand-violet)" },
  info: { label: "Info", color: "var(--ph-admin-blue)" },
  tip: { label: "Tip", color: "var(--ph-product-teal)" },
  hint: { label: "Tip", color: "var(--ph-product-teal)" },
  success: { label: "Success", color: "var(--ph-product-teal)" },
  check: { label: "Success", color: "var(--ph-product-teal)" },
  question: { label: "Question", color: "var(--ph-accent-purple)" },
  warning: { label: "Warning", color: "var(--ph-warm-gold)" },
  caution: { label: "Caution", color: "var(--ph-warm-gold)" },
  attention: { label: "Attention", color: "var(--ph-warm-gold)" },
  danger: { label: "Danger", color: "var(--danger)" },
  error: { label: "Error", color: "var(--danger)" },
  important: { label: "Important", color: "var(--ph-magenta)" },
  quote: { label: "Quote", color: "var(--ink-dim)" },
  abstract: { label: "Summary", color: "var(--ph-brand-teal)" },
  summary: { label: "Summary", color: "var(--ph-brand-teal)" },
};

function inline(text, ctx) {
  // wiki-links + inline embeds (inline embeds render as links; block embeds inline content)
  text = text.replace(/(!?)\[\[([^\]|\n]+?)(?:\|([^\]\n]+?))?\]\]/g, (_m, _bang, target, alias) => {
    const name = target.trim();
    const label = (alias || target).trim();
    const doc = ctx.resolve(name);
    if (doc) return `<a class="wikilink" data-doc-id="${doc.id}">${escape(label)}</a>`;
    return `<span class="wikilink unresolved" title="No note named &quot;${escape(name)}&quot;">${escape(label)}</span>`;
  });
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>");
  text = text.replace(/`([^`]+)`/g, "<code>$1</code>");
  return text;
}

function renderEmbed(name, ctx) {
  const doc = ctx.resolve(name);
  if (!doc) {
    return `<div class="md-embed missing">Can't find a note named "${escape(name)}"</div>`;
  }
  if (ctx.depth >= 1) {
    return `<div class="md-embed"><div class="md-embed-head" data-doc-id="${doc.id}">${escape(doc.name)}</div></div>`;
  }
  const body = parseFrontmatter(doc.markdown).body;
  const inner = renderBlocks(body, { ...ctx, depth: ctx.depth + 1 });
  return `<div class="md-embed"><div class="md-embed-head" data-doc-id="${doc.id}">${escape(doc.name)}</div><div class="md-embed-body">${inner}</div></div>`;
}

function renderBlocks(md, ctx) {
  md = (md || "").replace(/^---\n[\s\S]*?\n---\n?/, "");
  md = md.replace(/```action[\s\S]*?```/g, "");
  const lines = md.split("\n");
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const startI = i;
    const line = lines[i];

    // Fenced code
    if (line.startsWith("```")) {
      const close = lines.indexOf("```", i + 1);
      const body = lines.slice(i + 1, close < 0 ? lines.length : close).join("\n");
      out.push(`<pre style="background:var(--panel-3);padding:12px 14px;border-radius:8px;overflow-x:auto;font-size:13px;"><code>${escape(body)}</code></pre>`);
      i = close < 0 ? lines.length : close + 1;
      continue;
    }

    // Embed on its own line: ![[Note]] or ![[Note|alias]]
    const emb = line.match(/^!\[\[([^\]|\n]+?)(?:\|[^\]\n]+)?\]\]\s*$/);
    if (emb) {
      out.push(renderEmbed(emb[1].trim(), ctx));
      i++;
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      out.push(`<hr style="border:none;border-top:1px solid var(--hairline);margin:18px 0;" />`);
      i++;
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      out.push(`<h${level}>${inline(escape(h[2]), ctx)}</h${level}>`);
      i++;
      continue;
    }

    // Callout: > [!type] Optional title
    const c = line.match(/^>\s*\[!(\w+)\][+-]?\s*(.*)$/);
    if (c) {
      const type = c[1].toLowerCase();
      const meta = CALLOUTS[type] || { label: type.charAt(0).toUpperCase() + type.slice(1), color: "var(--ph-accent-purple)" };
      const title = c[2].trim() || meta.label;
      i++;
      const body = [];
      while (i < lines.length && lines[i].startsWith(">")) {
        body.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      const bodyHtml = body.join("\n").trim()
        ? `<div class="callout-body">${renderBlocks(body.join("\n"), ctx)}</div>`
        : "";
      out.push(`<div class="callout" data-callout="${escape(type)}" style="--callout-color:${meta.color}"><div class="callout-title">${inline(escape(title), ctx)}</div>${bodyHtml}</div>`);
      continue;
    }

    // Plain blockquote
    if (line.startsWith("> ")) {
      const block = [];
      while (i < lines.length && lines[i].startsWith("> ")) {
        block.push(lines[i].slice(2));
        i++;
      }
      out.push(`<blockquote>${inline(escape(block.join(" ")), ctx)}</blockquote>`);
      continue;
    }

    // Unordered list (incl. task lists)
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        let item = lines[i].replace(/^\s*[-*]\s+/, "");
        const task = item.match(/^\[( |x|X)\]\s+(.*)$/);
        if (task) {
          const checked = task[1].toLowerCase() === "x";
          items.push(
            `<li style="list-style:none;margin-left:-16px;display:flex;gap:8px;align-items:flex-start;">
              <span style="display:inline-block;width:14px;height:14px;border-radius:3px;border:1.5px solid ${checked ? "var(--good)" : "var(--ink-dim)"};background:${checked ? "var(--good)" : "transparent"};margin-top:4px;flex-shrink:0;"></span>
              <span style="${checked ? "color:var(--ink-dim);text-decoration:line-through;" : ""}">${inline(escape(task[2]), ctx)}</span>
            </li>`
          );
        } else {
          items.push(`<li>${inline(escape(item), ctx)}</li>`);
        }
        i++;
      }
      out.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(`<li>${inline(escape(lines[i].replace(/^\s*\d+\.\s+/, "")), ctx)}</li>`);
        i++;
      }
      out.push(`<ol>${items.join("")}</ol>`);
      continue;
    }

    // Blank line
    if (line.trim() === "") { i++; continue; }

    // Paragraph
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].startsWith("```") &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !lines[i].startsWith(">") &&
      !/^!\[\[/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^(-{3,}|\*{3,}|_{3,})\s*$/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    if (para.length > 0) out.push(`<p>${inline(escape(para.join(" ")), ctx)}</p>`);

    if (i === startI) i++;
  }

  return out.join("\n");
}

export default function Markdown({ text, className = "", docs = [], onOpenDoc }) {
  const index = React.useMemo(() => {
    const map = new Map();
    for (const d of docs || []) {
      if (d && d.name) {
        const k = d.name.toLowerCase();
        if (!map.has(k)) map.set(k, d);
      }
    }
    return map;
  }, [docs]);

  const ctx = React.useMemo(
    () => ({ resolve: (name) => index.get(String(name).toLowerCase()) || null, depth: 0 }),
    [index]
  );

  const html = renderBlocks(text || "", ctx);

  const onClick = (e) => {
    const el = e.target.closest("[data-doc-id]");
    if (el && onOpenDoc) {
      e.preventDefault();
      e.stopPropagation();
      onOpenDoc(el.getAttribute("data-doc-id"));
    }
  };

  return (
    <div
      className={`message-content ${className}`}
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
